import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import InvoiceForm from "@/pages/InvoiceForm";
import Customers from "@/pages/Customers";
import Reconciliation from "@/pages/Reconciliation";
import Staff from "@/pages/Staff";
import CreditNotes from "@/pages/CreditNotes";
import CreditNoteForm from "@/pages/CreditNoteForm";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Vendors from "@/pages/Vendors";
import HsnMaster from "@/pages/HsnMaster";
import CompanySettings from "@/pages/CompanySettings";
import GstRegister from "@/pages/GstRegister";
import PurchaseRegister from "@/pages/PurchaseRegister";
import GstSummary from "@/pages/GstSummary";
import Gstr1Portal from "@/pages/Gstr1Portal";
import Layout from "@/components/Layout";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  useEffect(() => {
    const TITLE = "TRAYAMBAK ENTERPRISE — GST Billing & Corporate Procurement";
    const DESC = "TRAYAMBAK ENTERPRISE — GST Billing, GeM tender fulfilment, and multi-category corporate procurement. Vadodara, Gujarat · GSTIN 24AMAPR1170Q1ZT.";
    const setMeta = () => {
      if (document.title !== TITLE) document.title = TITLE;
      const m = document.querySelector('meta[name="description"]');
      if (m && m.content !== DESC) m.setAttribute("content", DESC);
    };
    setMeta();
    // Guard against platform scripts that rewrite <title> after load
    const observer = new MutationObserver(setMeta);
    const titleEl = document.querySelector("title");
    if (titleEl) observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/invoices/new" element={<Protected><InvoiceForm /></Protected>} />
            <Route path="/invoices/:id/edit" element={<Protected><InvoiceForm /></Protected>} />
            <Route path="/credit-notes" element={<Protected><CreditNotes /></Protected>} />
            <Route path="/credit-notes/new" element={<Protected><CreditNoteForm /></Protected>} />
            <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
            <Route path="/hsn" element={<Protected><HsnMaster /></Protected>} />
            <Route path="/settings" element={<Protected><CompanySettings /></Protected>} />
            <Route path="/gst-register" element={<Protected><GstRegister /></Protected>} />
            <Route path="/purchase-register" element={<Protected><PurchaseRegister /></Protected>} />
            <Route path="/gst-summary" element={<Protected><GstSummary /></Protected>} />
            <Route path="/gstr1-portal" element={<Protected><Gstr1Portal /></Protected>} />
            <Route path="/reconciliation" element={<Protected><Reconciliation /></Protected>} />
            <Route path="/staff" element={<Protected><Staff /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
